import { FC, memo } from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { FundingShareType, UserRole } from 'src/@types/valueMatching/enums';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

const NCEMI_FUNDING_SHARES_BUNDLE = 'organisms.ncemiFundingSharesSection';

export interface NcemiFundingSharesSectionProps extends SectionBaseProps {
    /** Current seller share value */
    sellerShare: string;
    /** Callback to update the seller share */
    setSellerShare: (value: string) => void;
    /** Current brand share value */
    brandShare: string;
    /** Callback to update the brand share */
    setBrandShare: (value: string) => void;
    /** Current bank share value */
    bankShare: string;
    /** Callback to update the bank share */
    setBankShare: (value: string) => void;
    /** Current amazon share value */
    amazonShare: string;
    /** Callback to update the amazon share */
    setAmazonShare: (value: string) => void;
    /** Selected brand name */
    brandName: string;
    /** Callback to update the brand name */
    setBrandName: (value: string) => void;
    /** Brand name dropdown options */
    brandNameOptions: SelectProps.Option[];
    /** User role from campaign configuration */
    role: UserRole;
    /** Inline validation error for seller share */
    sellerShareError?: string;
    /** Inline validation error for brand share */
    brandShareError?: string;
    /** Inline validation error for bank share */
    bankShareError?: string;
    /** Inline validation error for amazon share */
    amazonShareError?: string;
    /** Inline validation error for brand name */
    brandNameError?: string;
    /** Form-level error (e.g., shares don't sum to 100) — shown above the fields */
    fundingSharesSumError?: string;
}

/**
 * Determines whether a funding share field is disabled based on the user role.
 *
 * Role-based enable/disable logic:
 * - SELLER role: seller, brand, amazon editable — bank disabled (all default 0%)
 * - BRAND role: ALL shares disabled (brand pre-set to 100%, others to 0%)
 * - AMAZON role: ALL shares editable (amazon pre-set to 100%, others to 0%)
 * - Otherwise: ALL shares disabled (fallback safety)
 */
function isShareDisabled(shareField: FundingShareType, role: UserRole): boolean {
    switch (role) {
        case UserRole.SELLER:
            // Sellers can edit seller, brand, and amazon shares — but NOT bank
            return shareField === FundingShareType.BANK;
        case UserRole.BRAND:
            // Brands cannot edit any share field
            return true;
        case UserRole.AMAZON:
            // Amazon can edit all share fields
            return false;
        default:
            // Unknown role — disable all
            return true;
    }
}

/**
 * NCEMI funding shares section — renders 4 number inputs for funding share
 * percentages (seller, brand, bank, amazon) with role-based enable/disable logic,
 * plus a Brand name dropdown next to the Brand share field.
 * Layout: Brand share row has "Brand share" input on left
 * and "Brand name" dropdown on right.
 * Only shown for NO_COST_EMI campaign type (parent controls visibility).
 * Owns its own i18n bundle (`organisms.ncemiFundingSharesSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const NcemiFundingSharesSection: FC<NcemiFundingSharesSectionProps> = ({
    sellerShare,
    setSellerShare,
    brandShare,
    setBrandShare,
    bankShare,
    setBankShare,
    amazonShare,
    setAmazonShare,
    brandName,
    setBrandName,
    brandNameOptions,
    role,
    mode,
    disabled,
    sellerShareError,
    brandShareError,
    bankShareError,
    amazonShareError,
    brandNameError,
    fundingSharesSumError,
}) => {
    const { translate } = useTranslate(NCEMI_FUNDING_SHARES_BUNDLE);
    // Funding shares are read-only after creation — disabled in both view and modify modes.
    const isViewDisabled = disabled ?? mode !== 'create';
    const errorI18n = { errorIconAriaLabel: translate('error_icon_aria_label') };

    /** Returns the error text for a funding share field, or undefined if the field is disabled/view-only. */
    const getShareError = (type: FundingShareType, err?: string) =>
        isViewDisabled || isShareDisabled(type, role) ? undefined : err;

    /** Returns whether a funding share field should show the invalid visual state. */
    const isShareInvalid = (type: FundingShareType, err?: string) =>
        !!err && !isViewDisabled && !isShareDisabled(type, role);

    const selectedBrandNameOption = brandNameOptions.find((o) => o.value === brandName) ?? null;

    return (
        <Container header={<Header variant="h2">{translate('section_funding_shares')}</Header>}>
            <SpaceBetween size={mode === 'view' ? 'xs' : 's'}>
                {fundingSharesSumError && !isViewDisabled && (
                    <Alert type="error">{fundingSharesSumError}</Alert>
                )}
                <FormField
                    label={translate('seller_share_label')}
                    errorText={getShareError(FundingShareType.SELLER, sellerShareError)}
                    i18nStrings={errorI18n}
                >
                    <Input
                        value={sellerShare}
                        onChange={({ detail }) => setSellerShare(detail.value)}
                        type="number"
                        disabled={isViewDisabled || isShareDisabled(FundingShareType.SELLER, role)}
                        ariaRequired={true}
                        data-testid="seller-share"
                        invalid={isShareInvalid(FundingShareType.SELLER, sellerShareError)}
                    />
                </FormField>
                <ColumnLayout columns={2}>
                    <FormField
                        label={translate('brand_share_label')}
                        errorText={getShareError(FundingShareType.BRAND, brandShareError)}
                        i18nStrings={errorI18n}
                    >
                        <Input
                            value={brandShare}
                            onChange={({ detail }) => setBrandShare(detail.value)}
                            type="number"
                            disabled={isViewDisabled || isShareDisabled(FundingShareType.BRAND, role)}
                            ariaRequired={true}
                            data-testid="brand-share"
                            invalid={isShareInvalid(FundingShareType.BRAND, brandShareError)}
                        />
                    </FormField>
                    <FormField
                        label={translate('brand_name_label')}
                        errorText={isViewDisabled ? undefined : brandNameError}
                        i18nStrings={errorI18n}
                    >
                        <Select
                            selectedOption={selectedBrandNameOption}
                            onChange={({ detail }) => setBrandName(detail.selectedOption.value ?? '')}
                            options={brandNameOptions}
                            disabled={isViewDisabled}
                            ariaRequired={true}
                            data-testid="brand-name"
                            invalid={!!brandNameError && !isViewDisabled}
                        />
                    </FormField>
                </ColumnLayout>
                <FormField
                    label={translate('bank_share_label')}
                    errorText={getShareError(FundingShareType.BANK, bankShareError)}
                    i18nStrings={errorI18n}
                >
                    <Input
                        value={bankShare}
                        onChange={({ detail }) => setBankShare(detail.value)}
                        type="number"
                        disabled={isViewDisabled || isShareDisabled(FundingShareType.BANK, role)}
                        ariaRequired={true}
                        data-testid="bank-share"
                        invalid={isShareInvalid(FundingShareType.BANK, bankShareError)}
                    />
                </FormField>
                <FormField
                    label={translate('amazon_share_label')}
                    errorText={getShareError(FundingShareType.AMAZON, amazonShareError)}
                    i18nStrings={errorI18n}
                >
                    <Input
                        value={amazonShare}
                        onChange={({ detail }) => setAmazonShare(detail.value)}
                        type="number"
                        disabled={isViewDisabled || isShareDisabled(FundingShareType.AMAZON, role)}
                        ariaRequired={true}
                        data-testid="amazon-share"
                        invalid={isShareInvalid(FundingShareType.AMAZON, amazonShareError)}
                    />
                </FormField>
            </SpaceBetween>
        </Container>
    );
};

export default memo(NcemiFundingSharesSection);
